'use client'

import React from 'react'
import { motion } from 'framer-motion'
import { cn } from '@/lib/utils'
import { useAccessibleMotion } from '@/hooks/useAccessibleMotion'

interface MotionHighlightProps {
  children: React.ReactNode
  className?: string
  highlightClassName?: string
  isActive?: boolean
  duration?: number
  delay?: number
}

const MotionHighlight = React.forwardRef<HTMLDivElement, MotionHighlightProps>(({
  children,
  className,
  highlightClassName = 'bg-primary/10',
  isActive = false,
  duration = 0.3,
  delay = 0,
}, ref) => {
  const { prefersReducedMotion, motionClassName } = useAccessibleMotion()
  
  const animationProps = prefersReducedMotion
    ? {
        // No motion for reduced motion users
        animate: { opacity: isActive ? 1 : 0.95 },
        transition: { duration: 0.01 },
      }
    : {
        // Full motion for users who haven't requested reduced motion
        animate: { scale: isActive ? 1.02 : 1 },
        transition: { duration, delay, ease: 'easeInOut' },
        whileHover: { scale: 1.02, transition: { duration: 0.2 } },
        whileTap: { scale: 0.98, transition: { duration: 0.1 } },
      }
  
  return (
    <motion.div
      ref={ref}
      className={cn('relative', className, motionClassName)}
      initial={false}
      {...animationProps}
    >
      <motion.div
        className={cn(
          'absolute inset-0 rounded-lg',
          highlightClassName
        )}
        initial={{ opacity: 0 }}
        animate={{
          opacity: isActive ? 1 : 0,
        }}
        transition={{
          duration: prefersReducedMotion ? 0.01 : duration,
          delay: prefersReducedMotion ? 0 : delay,
        }}
      />
      <div className="relative z-10">{children}</div>
    </motion.div>
  )
});

MotionHighlight.displayName = 'MotionHighlight';

export default MotionHighlight;